import React, { useState } from 'react';
import Icon from '../../components/AppIcon';
import LoginForm from './components/LoginForm';
import SignupForm from './components/SignupForm';
import PasswordRecoveryModal from './components/PasswordRecoveryModal';
import RoleSelector from './components/RoleSelector';
import SecurityBadges from './components/SecurityBadges';

const AuthenticationHub = () => {
  const [currentView, setCurrentView] = useState('login');

  return (
    <div className="min-h-screen bg-gradient-to-br from-fintech-blue-50 via-fintech-purple-50 to-fintech-teal-50">
      <div className="container mx-auto px-4 py-8 md:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            {/* Left Column - Branding & Security */}
            <div className="space-y-6 md:space-y-8 lg:space-y-10">
              {/* Logo & Branding */}
              <div className="text-center lg:text-left">
                <div className="inline-flex items-center gap-3 mb-4 md:mb-6">
                  <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl bg-gradient-to-br from-fintech-blue-500 to-fintech-purple-500 flex items-center justify-center shadow-glow-blue animate-pulse-slow">
                    <Icon name="TrendingUp" size={28} color="#FFFFFF" className="md:w-10 md:h-10" />
                  </div>
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-fintech-blue-600 to-fintech-purple-600 bg-clip-text text-transparent">FINVISAR</h1>
                </div>
                <p className="text-base md:text-lg lg:text-xl text-foreground font-semibold mb-2">
                  Financial Intelligence & Behavioral Analytics Platform
                </p>
                <p className="text-sm md:text-base text-muted-foreground">
                  Secure access to comprehensive financial data aggregation, predictive analytics, and behavioral finance advisory services
                </p>
              </div>

              {/* Security Badges */}
              <div className="hidden lg:block">
                <SecurityBadges />
              </div>

              {/* Role Selector */}
              <div className="hidden lg:block">
                <RoleSelector />
              </div>

              {/* Features List */}
              <div className="hidden lg:block p-6 bg-white/70 backdrop-blur-lg border border-fintech-blue-200 rounded-2xl shadow-elevation-3">
                <h3 className="text-lg font-semibold text-foreground mb-4">Platform Features</h3>
                <div className="space-y-3">
                  {[
                    { icon: 'Database', text: 'Real-time financial data aggregation', color: 'from-fintech-blue-500 to-fintech-teal-500' },
                    { icon: 'TrendingUp', text: 'Predictive analytics & scenario simulation', color: 'from-fintech-purple-500 to-fintech-blue-500' },
                    { icon: 'Brain', text: 'AI-powered behavioral finance insights', color: 'from-fintech-teal-500 to-fintech-green-500' },
                    { icon: 'Shield', text: 'Bank-grade security & encryption', color: 'from-fintech-green-500 to-fintech-teal-500' },
                    { icon: 'BarChart3', text: 'Comprehensive financial health scoring', color: 'from-fintech-blue-500 to-fintech-purple-500' },
                    { icon: 'FileText', text: 'Custom reports & data export', color: 'from-fintech-purple-500 to-fintech-teal-500' }
                  ]?.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3 group hover:translate-x-2 transition-smooth">
                      <div className={`flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br ${feature?.color} flex items-center justify-center shadow-elevation-1`}>
                        <Icon name={feature?.icon} size={18} color="#FFFFFF" />
                      </div>
                      <p className="text-sm text-foreground font-medium">{feature?.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Authentication Forms */}
            <div className="w-full">
              <div className="bg-white/80 backdrop-blur-xl border border-fintech-blue-200 rounded-3xl shadow-elevation-4 p-6 md:p-8 lg:p-10">
                {currentView === 'login' && (
                  <LoginForm
                    onSwitchToMFA={() => setCurrentView('login')}
                    onSwitchToRecovery={() => setCurrentView('recovery')}
                    onSwitchToSignup={() => setCurrentView('signup')}
                  />
                )}

                {currentView === 'signup' && (
                  <SignupForm
                    onSwitchToLogin={() => setCurrentView('login')}
                  />
                )}

                {/* Trust Indicators */}
                <div className="mt-6 md:mt-8 pt-6 border-t border-fintech-blue-200">
                  <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-xs md:text-sm text-muted-foreground">
                    <div className="flex items-center gap-2 hover:text-fintech-blue-600 transition-smooth">
                      <Icon name="Lock" size={14} />
                      <span>256-bit Encryption</span>
                    </div>
                    <div className="flex items-center gap-2 hover:text-fintech-purple-600 transition-smooth">
                      <Icon name="Shield" size={14} />
                      <span>SOC 2 Certified</span>
                    </div>
                    <div className="flex items-center gap-2 hover:text-fintech-teal-600 transition-smooth">
                      <Icon name="CheckCircle" size={14} />
                      <span>GDPR Compliant</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Help Section */}
              <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground mb-2">Need help accessing your account?</p>
                <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
                  <a href="#" className="text-fintech-blue-600 hover:text-fintech-purple-600 transition-smooth font-medium">
                    Contact Support
                  </a>
                  <span className="text-border">•</span>
                  <a href="#" className="text-fintech-blue-600 hover:text-fintech-purple-600 transition-smooth font-medium">
                    Documentation
                  </a>
                  <span className="text-border">•</span>
                  <a href="#" className="text-fintech-blue-600 hover:text-fintech-purple-600 transition-smooth font-medium">
                    System Status
                  </a>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-8 text-center text-xs text-muted-foreground">
                <p>© {new Date()?.getFullYear()} FINVISAR. All rights reserved.</p>
                <div className="flex flex-wrap items-center justify-center gap-3 mt-2">
                  <a href="#" className="hover:text-fintech-blue-600 transition-smooth">Privacy Policy</a>
                  <span>•</span>
                  <a href="#" className="hover:text-fintech-blue-600 transition-smooth">Terms of Service</a>
                  <span>•</span>
                  <a href="#" className="hover:text-fintech-blue-600 transition-smooth">Security</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Password Recovery Modal */}
      <PasswordRecoveryModal 
        isOpen={currentView === 'recovery'}
        onClose={() => setCurrentView('login')}
      />
    </div>
  );
};

export default AuthenticationHub;